"use client";

import { useState, useMemo } from 'react';
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarInset,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { generateSummaryAction } from '@/app/actions';
import { useToast } from '@/hooks/use-toast';
import { Logo } from './icons';
import { FileText, Loader2, Map, Newspaper, Tags, Link as LinkIcon, CheckCircle, Zap } from 'lucide-react';
import Link from 'next/link';
import type { SummarizeDailyNewsOutput } from '@/ai/flows/summarize-daily-news';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ALL_NEWS_SOURCES, NewsSource } from '@/lib/sources';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu } from 'lucide-react';

export function Dashboard() {
  const [summary, setSummary] = useState<SummarizeDailyNewsOutput | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedSources, setSelectedSources] = useState<NewsSource[]>([ALL_NEWS_SOURCES[3], ALL_NEWS_SOURCES[4]]);
  const { toast } = useToast();

  const handleGenerateSummary = async () => {
    if (selectedSources.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Aucune source sélectionnée',
        description: 'Veuillez sélectionner au moins une source d\'actualités.',
      });
      return;
    }
    setIsLoading(true);
    setSummary(null);
    try {
      const result = await generateSummaryAction({ mediaSources: selectedSources.map(s => s.name) });
      setSummary(result);
    } catch (error) {
      console.error(error);
      toast({
        variant: 'destructive',
        title: 'Erreur lors de la génération du résumé',
        description: 'Une erreur inattendue s\'est produite. Veuillez réessayer plus tard.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceChange = (source: NewsSource) => {
    setSelectedSources(prev => 
      prev.some(s => s.id === source.id) 
        ? prev.filter(s => s.id !== source.id) 
        : [...prev, source]
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b bg-background px-4 md:px-6">
        <Link href="/" className="flex items-center gap-2 font-bold" prefetch={false}>
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Logo className="size-5" />
          </div>
          <span className="font-headline text-xl">My Right Info</span>
        </Link>
        <h2 className="font-headline text-xl font-bold hidden md:block">Briefing géopolitique quotidien</h2>
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon">
                <Menu className="size-5" />
                <span className="sr-only">Ouvrir le menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
                <div className="p-4">
                  <h3 className="font-bold mb-4">Sélection des sources</h3>
                   <div className="flex flex-col gap-2">
                        {ALL_NEWS_SOURCES.map(source => {
                            const isSelected = selectedSources.some(s => s.id === source.id);
                            return (
                                <button
                                    key={source.id}
                                    onClick={() => handleSourceChange(source)}
                                    className={cn(
                                        "min-h-[44px] w-full flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-all",
                                        isSelected 
                                            ? "bg-primary text-primary-foreground border-transparent shadow-md" 
                                            : "bg-muted text-muted-foreground hover:bg-secondary"
                                    )}
                                >
                                    {isSelected && <CheckCircle className="size-4" />}
                                    {source.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      <main className="flex-1 p-4 md:p-6 lg:p-8 flex flex-col md:flex-row gap-8">
        <div className="hidden md:block md:w-1/3 lg:w-1/4 space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>1. Choisissez vos sources</CardTitle>
                    <CardDescription>Sélectionnez les médias que l'IA doit analyser.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-2">
                        {ALL_NEWS_SOURCES.map(source => {
                            const isSelected = selectedSources.some(s => s.id === source.id);
                            return (
                                <button
                                    key={source.id}
                                    onClick={() => handleSourceChange(source)}
                                    className={cn(
                                        "min-h-[44px] flex-grow flex items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all",
                                        isSelected 
                                            ? "bg-primary text-primary-foreground border-transparent shadow-md" 
                                            : "bg-muted text-muted-foreground hover:bg-secondary"
                                    )}
                                >
                                    {isSelected && <CheckCircle className="size-4" />}
                                    {source.name}
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
        <div className="flex-1 flex flex-col gap-4">
           <Button
              onClick={handleGenerateSummary}
              disabled={isLoading || selectedSources.length === 0}
              className="w-full h-14 text-lg font-bold shadow-lg"
              size="lg"
            >
              {isLoading ? (
                <Loader2 className="mr-2 animate-spin" />
              ) : (
                <Zap className="mr-2"/>
              )}
              Générer mon briefing
            </Button>
            {isLoading ? (
                <LoadingState />
            ) : summary ? (
                <SummaryDisplay summary={summary} />
            ) : (
                <InitialState />
            )}
        </div>
      </main>
    </div>
  );
}

function InitialState() {
  return (
    <div className="flex h-full items-center justify-center rounded-xl border-2 border-dashed">
        <div className="text-center p-8">
            <div className="mx-auto w-fit rounded-full bg-secondary p-4">
                <Newspaper className="size-10 text-muted-foreground" />
            </div>
            <h3 className="mt-4 font-headline text-2xl font-bold">Votre briefing personnalisé vous attend</h3>
            <p className="mt-2 max-w-md text-muted-foreground">
                L'IA est prête à synthétiser l'actualité. Choisissez vos sources et cliquez sur "Générer" pour recevoir votre condensé intelligent.
            </p>
        </div>
    </div>
  );
}

function LoadingState() {
    return (
        <Card>
            <CardHeader>
                 <Skeleton className="h-8 w-1/2" />
                 <Skeleton className="h-4 w-1/4" />
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                </div>
                 <div className="space-y-2">
                    <Skeleton className="h-4 w-4/5" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-full" />
                </div>
                <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-5/6" />
                </div>
                <div className="flex justify-center pt-4">
                    <Loader2 className="size-8 animate-spin text-primary" />
                </div>
                <p className="text-center text-muted-foreground">L'IA lit et synthétise des centaines d'articles pour vous... un instant.</p>
            </CardContent>
        </Card>
    );
}

function SummaryDisplay({ summary }: { summary: SummarizeDailyNewsOutput }) {
    const today = new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
    
    const newsItems = summary?.news;

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 font-headline text-3xl">
                    <FileText className="text-primary"/>
                    Briefing du jour
                </CardTitle>
                <CardDescription>{today}</CardDescription>
            </CardHeader>
            <CardContent>
                 <div className="prose prose-lg max-w-none text-foreground">
                    {newsItems && newsItems.length > 0 ? (
                        <ol className="list-decimal space-y-6 pl-5">
                            {newsItems.map((item, index) => (
                                item.title && (
                                    <li key={index} className="pl-2">
                                        <h4 className="font-semibold">{item.title}</h4>
                                        <p className="text-base">{item.summary}</p>
                                        {item.sourceUrl && (
                                            <Button asChild size="sm" variant="outline" className="mt-2">
                                                <Link href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
                                                    <LinkIcon className="mr-2 size-4" />
                                                    Lire la source
                                                </Link>
                                            </Button>
                                        )}
                                    </li>
                                )
                            ))}
                        </ol>
                    ) : (
                        <p>Aucune actualité n'a pu être générée. Veuillez réessayer avec d'autres sources.</p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
